import React from "react";
import {
  FaCheck,
  FaClock,
  FaEuroSign,
  FaEye,
  FaPen,
  FaTimes,
  FaTrashAlt,
  FaUsers,
  FaUserSlash,
} from "react-icons/fa";

import {
  formatCurrency,
  formatNumber,
  getColorClass,
  getStatusClass,
  getStatusLabel,
} from "../utils/groupesUtils";

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function GroupSkeleton() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-5 w-32 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      <div className="mt-4 h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="h-12 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-12 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function GroupesGrid({
  loading,
  groupes = [],
  deleteCandidateId,
  onDetails,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <GroupSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!groupes.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucun groupe trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez un nouveau groupe.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {groupes.map((group) => {
        const isDeleting = deleteCandidateId === group.id;

        return (
          <article
            key={group.id}
            className={`overflow-hidden rounded-3xl border bg-white shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:bg-slate-900 dark:shadow-none ${
              group.is_active
                ? "border-slate-200 dark:border-slate-800"
                : "border-slate-200 opacity-75 dark:border-slate-800"
            }`}
          >
            <div className={`h-2 ${group.color_groupe || "bg-slate-400"}`} />

            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${getColorClass(
                        group.color_groupe
                      )}`}
                    >
                      {group.nom || "Groupe"}
                    </span>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                        group.is_active
                      )}`}
                    >
                      {getStatusLabel(group.is_active)}
                    </span>
                  </div>

                  <h3 className="mt-4 truncate text-xl font-bold text-slate-950 dark:text-white">
                    {group.nom}
                  </h3>

                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Ordre d’affichage : {group.ordre_groupe || "—"}
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 px-3 py-2 text-right dark:bg-slate-950">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Quota
                  </p>
                  <p className="text-lg font-bold text-slate-950 dark:text-white">
                    {group.quota_groupe || "—"}
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-orange-600 dark:bg-slate-900 dark:text-orange-300">
                    <FaClock />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      Horaires
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {group.horaire1 || "Horaire principal non renseigné"}
                    </p>
                    {group.horaire2 && (
                      <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                        {group.horaire2}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      <FaUsers />
                      Naissance
                    </div>
                    <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {group.annees_naissance || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      <FaEuroSign />
                      Tarifs
                    </div>
                    <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {formatCurrency(group.tarif_licence)} licence
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {formatCurrency(group.tarif_cotisation)} cotisation
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Enseignants
                  </p>
                  <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {[group.enseignant1, group.enseignant2]
                      .filter(Boolean)
                      .join(", ") || "Non renseigné"}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                {isDeleting ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onConfirmDelete(group.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaCheck />
                      Confirmer
                    </button>

                    <button
                      type="button"
                      onClick={onCancelDelete}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <IconButton
                      title="Voir le détail"
                      icon={FaEye}
                      tone="blue"
                      onClick={() => onDetails(group)}
                    />

                    <IconButton
                      title="Modifier"
                      icon={FaPen}
                      tone="orange"
                      onClick={() => onEdit(group)}
                    />

                    <IconButton
                      title="Supprimer"
                      icon={FaTrashAlt}
                      tone="rose"
                      onClick={() => onAskDelete(group.id)}
                    />
                  </>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}